<template>
  <form ref="formRef" novalidate @submit.prevent="onSubmit">
    <slot :valid="valid" :validate="validate" :reset="reset" />
  </form>
</template>

<script setup lang="ts">
import { provide, ref } from 'vue'

import { type UiFormChild, UiFormContextKey } from './form-context'

/**
 * UiForm — Vuetify <v-form> replacement.
 * Provides validation via custom rule registry for child fields.
 * Exposes `valid`, `validate()`, and `reset()` via scoped slot props and
 * via template ref for Options API consumers.
 */
const props = withDefaults(
  defineProps<{ lazyValidation?: boolean; modelValue?: boolean }>(),
  {
    lazyValidation: false,
    modelValue: true,
  },
)

const emit = defineEmits<{
  (e: 'submit', event: Event): void
  (e: 'update:modelValue', value: boolean): void
}>()

const formRef = ref<HTMLFormElement | null>(null)
const children = ref<UiFormChild[]>([])

/** Latest validity reported per child field. The form's overall validity is
 * the conjunction of these, recomputed live as fields validate — this is what
 * lets `v-model="valid"` (e.g. disabled submit buttons) track the form as the
 * user types, like Vuetify's v-form @input. */
const childValidity = ref(new Map<string, boolean>())

const valid = ref(props.modelValue)

function recomputeValidity(): boolean {
  let allValid = true
  for (const child of children.value) {
    // Children report via onValidated as they validate (eager mode reports
    // on every input and seeds its real validity silently on mount; lazy
    // mode on blur). A child with no report yet counts as valid — in lazy
    // mode an untouched form is valid until proven otherwise (Vuetify).
    if (childValidity.value.get(child.id) === false) {
      allValid = false
    }
  }
  return allValid
}

provide(UiFormContextKey, {
  register: (child) => {
    children.value.push(child)
    // Valid until the child reports: in non-lazy mode it seeds its real
    // validity right after registering (useValidation), so an empty required
    // field makes the form invalid without showing an error — like v-form.
    childValidity.value.set(child.id, true)
  },
  unregister: (id) => {
    const index = children.value.findIndex((c) => c.id === id)
    if (index !== -1) children.value.splice(index, 1)
    childValidity.value.delete(id)
    valid.value = recomputeValidity()
    emit('update:modelValue', valid.value)
  },
  lazyValidation: props.lazyValidation,
  onValidated: (id, isValid) => {
    childValidity.value.set(id, isValid)
    valid.value = recomputeValidity()
    emit('update:modelValue', valid.value)
  },
})

function validate(): boolean {
  let isValid = true
  for (const child of children.value) {
    if (!child.validate()) {
      // child.validate() reports to onValidated, which sets childValidity;
      // mirror here to keep the map consistent even if a child was
      // registered without the callback (older consumers).
      childValidity.value.set(child.id, false)
      isValid = false
    }
  }
  valid.value = isValid
  emit('update:modelValue', isValid)
  return isValid
}

function reset(): void {
  children.value.forEach((child) => {
    if (child.reset) child.reset()
    child.resetValidation()
    childValidity.value.set(child.id, true)
    // Non-lazy: errors are cleared but untouched invalid fields still count.
    if (!props.lazyValidation) child.seedValidity?.()
  })
  valid.value = recomputeValidity()
  emit('update:modelValue', valid.value)
}

function onSubmit(e: Event) {
  if (validate()) {
    emit('submit', e)
  }
}

defineExpose({ validate, reset, valid })
</script>
